import type { PopoutPosition } from '../../modules/softphone/components/SoftphonePopout/index.types';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DraggableEvent } from 'react-draggable';
import { DraggableData } from 'react-rnd';
import { UseDraggablePositionProps, UseDraggablePositionReturn } from './index.types';

// Debounce delay for resize events (ms)
const RESIZE_DEBOUNCE_DELAY = 150;
// Minimum Y position (header must always be visible for dragging)
const MIN_Y_POSITION = 0;
// Minimum X position (ensure drag handle is accessible)
const MIN_X_POSITION = 0;
// Header height that must remain visible (for drag handle)
const HEADER_HEIGHT = 50;

/**
 * Hook to manage draggable popout position with localStorage persistence
 * and window resize handling
 *
 * Based on legacy Softphone behavior with bounds checking that accounts
 * for actual component height, not just minimum height.
 *
 * Performance optimizations:
 * - Uses refs to avoid unnecessary re-renders
 * - Debounces resize events
 * - Memoizes callbacks with stable dependencies
 * - Batches localStorage writes
 */
export const useDraggablePosition = ({
  storageKey,
  defaultPosition,
  width,
  minHeight = 550, // Default to realistic height estimate (matches legacy)
}: UseDraggablePositionProps): UseDraggablePositionReturn => {
  const [position, setPosition] = useState<PopoutPosition>(() => {
    // Initialize with saved position if available (avoids flash)

    if (typeof window === 'undefined') {
      return defaultPosition;
    }

    const { innerWidth, innerHeight } = window;

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as PopoutPosition;
        const savedX = parsed.x ?? defaultPosition.x;
        const savedY = parsed.y ?? defaultPosition.y;

        // Validate saved position against current window bounds
        let newX = savedX;
        let newY = savedY;

        // Ensure header stays visible (never negative Y)
        newY = Math.max(MIN_Y_POSITION, newY);
        newX = Math.max(MIN_X_POSITION, newX);

        // Check if right edge goes beyond window
        if (savedX + width > innerWidth) {
          newX = Math.max(MIN_X_POSITION, innerWidth - width);
        }

        // Check if bottom edge goes beyond window
        if (savedY + minHeight > innerHeight) {
          newY = Math.max(MIN_Y_POSITION, innerHeight - minHeight);
        }

        // Final safety: ensure header is always in viewport
        newY = Math.max(MIN_Y_POSITION, Math.min(newY, innerHeight - HEADER_HEIGHT));

        return { x: newX, y: newY };
      }
    } catch {
      // Fall through to default
    }

    // Calculate dynamic default if no saved position
    const dynamicDefault = {
      x: Math.max(MIN_X_POSITION, innerWidth - width - 70),
      y: innerHeight > minHeight + 80 ? 80 : MIN_Y_POSITION,
    };
    return dynamicDefault;
  });

  // Store dimensions and storage key in refs to avoid recreating callbacks
  const dimensionsRef = useRef({ width, minHeight });
  const storageKeyRef = useRef(storageKey);
  const resizeTimeoutRef = useRef<number>();

  // Update refs when props change (doesn't trigger re-renders)
  useEffect(() => {
    dimensionsRef.current = { width, minHeight };
    storageKeyRef.current = storageKey;
  }, [width, minHeight, storageKey]);

  /**
   * Validates and constrains position within window bounds
   * Ensures the header/drag handle is always visible and accessible:
   * - Header must stay within viewport (Y >= 0)
   * - At least part of the width must be visible for dragging
   * - Adjusts position when window is resized smaller
   *
   * Note: react-rnd handles bounds during drag, but we need this for:
   * - Window resize events (including dev tools opening)
   * - Loading saved positions that may be out of bounds
   * - Dynamic dimension changes
   */
  const constrainPosition = useCallback((pos: PopoutPosition): PopoutPosition => {
    const { width: w, minHeight: h } = dimensionsRef.current;
    const { innerWidth, innerHeight } = window;

    let newX = pos.x;
    let newY = pos.y;

    // Ensure header stays visible (never negative Y)
    newY = Math.max(MIN_Y_POSITION, newY);

    // Ensure left edge doesn't go negative
    newX = Math.max(MIN_X_POSITION, newX);

    // Check if right edge goes beyond window - keep some width visible
    if (newX + w > innerWidth) {
      newX = Math.max(MIN_X_POSITION, innerWidth - w);
    }

    // Check if bottom edge goes beyond window - prioritize header visibility
    // If window is too small, header stays at top
    if (newY + h > innerHeight) {
      newY = Math.max(MIN_Y_POSITION, innerHeight - h);
    }

    // Final safety check: ensure header is always in viewport
    newY = Math.max(MIN_Y_POSITION, Math.min(newY, innerHeight - HEADER_HEIGHT));

    // Early return if position didn't change (common case)
    if (pos.x === newX && pos.y === newY) {
      return pos;
    }

    return { x: newX, y: newY };
  }, []);

  /**
   * Saves position to localStorage (non-blocking)
   * Memoized with empty deps since it only uses refs
   */
  const savePosition = useCallback((pos: PopoutPosition) => {
    try {
      localStorage.setItem(storageKeyRef.current, JSON.stringify(pos));
    } catch (error) {
      // Silently fail if localStorage is full or unavailable
      console.warn('[useDraggablePosition] Failed to save position:', error);
    }
  }, []);

  /**
   * Updates position with validation and persistence
   * Note: react-rnd bounds="window" handles constraints during drag,
   * but we still validate here as a safety net for edge cases
   */
  const updatePosition = useCallback(
    (newPosition: PopoutPosition) => {
      const constrained = constrainPosition(newPosition);

      // Only update if position actually changed
      setPosition((prev) => {
        if (prev.x === constrained.x && prev.y === constrained.y) {
          return prev;
        }
        // Save to localStorage after state update
        savePosition(constrained);
        return constrained;
      });
    },
    [constrainPosition, savePosition]
  );

  /**
   * Debounced resize handler
   * Memoized with stable dependencies
   */
  const handleResize = useCallback(() => {
    // Clear existing timeout
    if (resizeTimeoutRef.current) {
      window.clearTimeout(resizeTimeoutRef.current);
    }

    // Debounce resize events
    resizeTimeoutRef.current = window.setTimeout(() => {
      setPosition((prev) => {
        const constrained = constrainPosition(prev);

        // Only update if position changed
        if (prev.x === constrained.x && prev.y === constrained.y) {
          return prev;
        }

        savePosition(constrained);
        return constrained;
      });
    }, RESIZE_DEBOUNCE_DELAY);
  }, [constrainPosition, savePosition]);

  // Setup resize listener
  useEffect(() => {
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      // Clear pending timeout on unmount
      if (resizeTimeoutRef.current) {
        window.clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [handleResize]);

  /**
   * Handle drag stop
   * Memoized with stable dependencies
   */
  const onDragStop = useCallback(
    (_e: DraggableEvent, data: DraggableData) => {
      updatePosition({ x: data.x, y: data.y });
    },
    [updatePosition]
  );

  // Memoize return value to prevent unnecessary re-renders in parent
  return useMemo(
    () => ({
      position,
      onDragStop,
    }),
    [position, onDragStop]
  );
};
